import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link as BaseLink, useNavigate } from "react-router";
import { routeKeys, startTravellingToVisit } from "@/api/Routes";
import Address from "@/components/Address/Address";
import { FilledButton } from "@medoma/components/Button/Button";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import { StatusBar } from "@/components/StatusBar/StatusBar";
import { Time } from "@/components/Time/Time";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import CheckmarkIcon from "@medoma/components/icons/CheckmarkIcon";
import {
  deducedError,
  displayErrorMessageAlert,
  isErrorWithKnownErrorCode,
  knownErrorCodeSchema,
} from "@/Utils/ErrorUtils";
import styles from "./VisitCard.module.scss";
import type { IVisit } from "@medoma/models/visits";
import { calculateVisitTimes } from "@medoma/models/visits";
import { getPatientNameWithStatus } from "@/api/Patients";
import HiddenIcon from "@medoma/components/icons/HiddenIcon";
import * as Sentry from "@sentry/react";
import { employeeName } from "@medoma/models/shifts";
import { categorySchema, timeOfDayDictionary } from "@medoma/models/activities";
import { Text, TextWithLineBreaks } from "@medoma/components/Text/Text";
import { i18n } from "@lingui/core";
import {
  patientStatusSchema,
  type IDeletedPatient,
  type IExistingPatient,
} from "@medoma/models/patients";
import CommentIcon from "@medoma/components/icons/CommentIcon";
import ActivityDescriptionIcon from "@medoma/components/icons/ActivityDescriptionIcon";
import { StatusTag } from "@/components/StatusTag/StatusTag";
import { Heading } from "@medoma/components/Heading/Heading";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { useUserProfile } from "@/api/Users";
import { ImportantInfo } from "@/components/ImportantInfo/ImportantInfo";
import { humanFormat } from "@medoma/models/date-and-time";
import { useSelectedDateIsToday } from "@/Utils/useSelectedDate";

interface IVisitCard {
  isUpcomingVisit: boolean;
  visit: IVisit;
  routeId: string;
}

const PatientName = ({ name }: { name: string }) => {
  return (
    <Heading level="h3" className={styles.name}>
      {name}
    </Heading>
  );
};

const getTimespan = (visit: IVisit) => {
  const visitTimes = calculateVisitTimes(visit);
  if (visitTimes.isAnyTimeOfDay) {
    return i18n._(timeOfDayDictionary.Any.short);
  }
  if (visitTimes.start === undefined) {
    return "";
  }
  if (visitTimes.end === undefined) {
    return "";
  }
  return formattedTimeSpan(visitTimes.start, visitTimes.end);
};

const PatientAddress = ({
  patient,
}: {
  patient:
    | IDeletedPatient
    | Pick<IExistingPatient, "address" | "status" | "flags">;
}) => {
  const { countryCode } = useUserProfile();
  if (patient.status === patientStatusSchema.enum.deleted) {
    return null;
  }
  return (
    <Address
      address={{
        ...patient.address,
        countryCode,
      }}
    />
  );
};

const VisitStartedAt = ({ startedAt }: { startedAt: Date }) => {
  const visitStartedAtTime = humanFormat(startedAt, { timeStyle: "short" });
  return (
    <Text element="div" color="faded">
      <Trans>Startat {visitStartedAtTime}</Trans>
    </Text>
  );
};

const VisitFinishedAt = ({ finishedAt }: { finishedAt: Date }) => {
  return (
    <StatusTag
      status="finished"
      timestamp={finishedAt}
      size="medium"
      variant="icon-and-text-with-timestamp"
    />
  );
};

const VisitCard = ({ visit, isUpcomingVisit, routeId }: IVisitCard) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { countryCode } = useUserProfile();
  const selectedDateIsToday = useSelectedDateIsToday();
  const { mutate: startTravellingToVisitMutation, isPending } = useMutation({
    mutationFn: ({ routeId, visitId }: { routeId: string; visitId: string }) =>
      startTravellingToVisit(routeId, visitId),
    onSuccess: async () => {
      // Navigate before refetch for snappy UI
      void navigate(`${routeId}/visits/${visit.id}`);
      await queryClient.refetchQueries({ queryKey: routeKeys.all });
    },
    onError: async (error) => {
      if (
        isErrorWithKnownErrorCode(error) &&
        (error.data.code ===
          knownErrorCodeSchema.enum.AlreadyTravellingToVisit ||
          error.data.code === knownErrorCodeSchema.enum.AlreadyTravelledToVisit)
      ) {
        Sentry.captureException(
          "Failed to start travelling to visit from VisitCard. Visit was already being travelled to. Refetching and re-navigating.",
        );
        // Navigate before refetch for snappy UI
        void navigate(`${routeId}/visits/${visit.id}`);
        await queryClient.refetchQueries({ queryKey: routeKeys.all });
      } else {
        displayErrorMessageAlert(
          `${t`Det gick inte att påbörja resan till besöket.`} ${deducedError(error)}`,
        );
      }
    },
  });

  const isActiveVisit =
    visit.status === "travellingTo" || visit.status === "ongoing";
  const canStartTravellingToVisit = isUpcomingVisit && selectedDateIsToday;

  return (
    <li className={styles.visitCard}>
      <BaseLink
        to={`${routeId}/visits/${visit.id}`}
        className={styles.cardLink}
      >
        {visit.status === "finished" ? (
          <div className={styles.finishedVisit}>
            <div className={styles.iconAndName}>
              <CategoryIcon category={categorySchema.enum.HomeVisit} />
              <PatientName
                name={getPatientNameWithStatus(visit.patient, "full")}
              />
            </div>
            {visit.finishedAt ? (
              <VisitFinishedAt finishedAt={visit.finishedAt} />
            ) : (
              <CheckmarkIcon />
            )}
          </div>
        ) : (
          <>
            <header className={styles.header}>
              <div className={styles.topRow}>
                <div className={styles.iconAndName}>
                  <CategoryIcon category={categorySchema.enum.HomeVisit} />
                  <PatientName
                    name={getPatientNameWithStatus(visit.patient, "full")}
                  />
                </div>
                {isActiveVisit && visit.startedAt ? (
                  <VisitStartedAt startedAt={visit.startedAt} />
                ) : (
                  <Time>{getTimespan(visit)}</Time>
                )}
              </div>
              <PatientAddress patient={visit.patient} />
              {visit.patient.status !== patientStatusSchema.enum.deleted ? (
                <ImportantInfo patient={visit.patient} />
              ) : null}
            </header>
            <ul className={styles.occurrences}>
              {visit.occurrences.map((occurrence) => {
                return (
                  <li key={occurrence.id}>
                    <div className={styles.activityTitle}>
                      <Text element="div">{occurrence.title}</Text>
                      {occurrence.hidden ? <HiddenIcon /> : null}
                    </div>
                    {occurrence.description ? (
                      <div className={styles.description}>
                        <ActivityDescriptionIcon />
                        <TextWithLineBreaks size="small" color="faded">
                          {occurrence.description}
                        </TextWithLineBreaks>
                      </div>
                    ) : null}
                    {occurrence.note ? (
                      <div className={styles.description}>
                        <CommentIcon />
                        <TextWithLineBreaks size="small" color="faded">
                          {occurrence.note}
                        </TextWithLineBreaks>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            <ul className={styles.assignees}>
              {visit.assignees.map((shift) => {
                return (
                  <li key={shift.id}>
                    <CompetenceChip
                      competence={shift.competence}
                      state="neutral"
                      content={
                        shift.employee
                          ? employeeName(shift.employee, {
                              countryCode,
                              viewer: "colleague",
                            })
                          : ""
                      }
                    />
                  </li>
                );
              })}
            </ul>
          </>
        )}
        {isActiveVisit ? (
          <div className={styles.statusBarWrapper}>
            <StatusBar>
              {visit.status === "travellingTo" ? (
                <Trans>Pågående resa</Trans>
              ) : (
                <Trans>Pågående besök</Trans>
              )}
            </StatusBar>
          </div>
        ) : (
          <></>
        )}
      </BaseLink>
      {canStartTravellingToVisit ? (
        <footer className={styles.footer}>
          <FilledButton
            onClick={() =>
              startTravellingToVisitMutation({
                routeId: routeId,
                visitId: visit.id,
              })
            }
            disabled={isPending}
            width="fill-container"
          >
            <Trans>Starta resa</Trans>
          </FilledButton>
        </footer>
      ) : (
        <></>
      )}
    </li>
  );
};

export default VisitCard;
